import { useState, useEffect } from "react";
import { useTheme } from "@/lib/ThemeContext";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { Sparkles, X, Tag } from "lucide-react";
import logger from "@/lib/logger";

const SEEN_KEY = "mmntm_last_seen_version";

export default function WhatsNewModal() {
  const { tokens: t } = useTheme();
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.getWhatsNew().then((d) => {
      if (!d?.version || typeof d.version !== "string") return;
      const seen = localStorage.getItem(SEEN_KEY);
      const safeSeen = typeof seen === "string" && seen.length < 50 ? seen : null;
      if (safeSeen !== d.version && d.release_notes) {
        setData(d);
        setShow(true);
      }
    }).catch((e) => logger.error(e));
  }, [user]);

  const handleDismiss = () => {
    if (data?.version && typeof data.version === "string" && data.version.length < 50) {
      localStorage.setItem(SEEN_KEY, data.version);
    }
    setShow(false);
  };

  if (!show || !data) return null;

  const txtH = "text-sm font-semibold";
  const txtB = "text-[13px]";
  const txtS = "text-[11px] font-medium";

  return (
    <div className="fixed inset-0 z-[997] flex items-center justify-center" data-testid="whats-new-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleDismiss} />
      {/* H-1: cap the panel to the viewport (divided by the theme zoom, which scales dvh units) and let it scroll — an oversized panel on a phone hid "Got it!" and left the app unreachable behind the overlay. */}
      <div className="relative z-10 rounded-2xl overflow-y-auto max-w-md w-full mx-4"
        style={{ background: t.bgCard, border: `1px solid ${t.borderColor}`, boxShadow: "0 24px 48px rgba(0,0,0,0.3)", maxHeight: "calc((100dvh - 2rem) / var(--tm-zoom, 1))" }}>
        {/* Header */}
        <div className="px-5 py-4 flex items-center gap-3" style={{ borderBottom: `1px solid ${t.borderColor}` }}>
          <Sparkles className="h-5 w-5" style={{ color: t.accent }} />
          <div className="flex-1">
            <span className={txtH} style={{ color: t.textPrimary }}>What's New</span>
            <div className="flex items-center gap-1.5 mt-0.5">
              <Tag className="h-2.5 w-2.5" style={{ color: t.accent }} />
              <span className={txtS} style={{ color: t.accent }}>v{data.version}</span>
            </div>
          </div>
          <button className="p-1 rounded-lg hover:bg-white/5 transition-all" onClick={handleDismiss}
            style={{ color: t.textMuted }} data-testid="whats-new-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4">
          <p className={`${txtB} mb-4 leading-relaxed`} style={{ color: t.textSecondary }}>{data.release_notes}</p>

          {data.features?.length > 0 && (
            <div>
              <span className={`${txtS} block mb-2`} style={{ color: t.textMuted }}>New Features</span>
              <div className="space-y-2">
                {data.features.map((f) => (
                  <div key={f.key} className="flex items-start gap-2 py-1.5 px-2 rounded-lg"
                    style={{ background: `${t.accent}06` }}>
                    <div className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: t.accent }} />
                    <div>
                      <span className={`${txtB} font-medium block`} style={{ color: t.textPrimary }}>{f.name}</span>
                      {f.description && <span className={txtS} style={{ color: t.textMuted }}>{f.description}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3" style={{ borderTop: `1px solid ${t.borderColor}` }}>
          <button className={`w-full ${txtS} py-2.5 rounded-lg transition-all`}
            style={{ background: t.accent, color: "#fff" }}
            onClick={handleDismiss} data-testid="whats-new-dismiss">
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
}
